import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { AddPhoneNumberDialog } from './AddPhoneNumberDialog';

interface ChecklistStep {
  key: string;
  label: string;
  description: string;
  /** Navigation target. Omitted for steps handled in-place via `action`. */
  route?: string;
  /** Handled without leaving the dashboard (e.g. opens a dialog). */
  action?: () => void;
  cta: string;
  done: boolean;
}

export function OnboardingChecklist() {
  const { user, apiKeys } = useAuth();
  const navigate = useNavigate();

  const [hasPhoneNumber, setHasPhoneNumber] = useState(false);
  const [hasScript, setHasScript] = useState(false);
  const [hasCampaign, setHasCampaign] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [showAddPhoneNumber, setShowAddPhoneNumber] = useState(false);

  const dismissKey = user?.id ? `onboarding-dismissed-${user.id}` : null;

  useEffect(() => {
    if (!dismissKey) return;
    setDismissed(localStorage.getItem(dismissKey) === 'true');
  }, [dismissKey]);

  const fetchStatus = useCallback(async () => {
    if (!user?.id) return;

    const [phonesResult, scriptsResult, campaignsResult] = await Promise.all([
      supabase
        .from('phone_numbers')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id),
      supabase
        .from('scripts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id),
      supabase
        .from('campaigns')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id),
    ]);

    setHasPhoneNumber((phonesResult.count ?? 0) > 0);
    setHasScript((scriptsResult.count ?? 0) > 0);
    setHasCampaign((campaignsResult.count ?? 0) > 0);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const hasApiKeys = !!(
    apiKeys &&
    (apiKeys.vapi || apiKeys.twilio || apiKeys.twilioSid || apiKeys.openai || apiKeys.elevenlabs)
  );

  const steps: ChecklistStep[] = [
    {
      key: 'api_keys',
      label: 'Connect your API keys',
      description: 'Link VAPI, Twilio, and your AI provider to power calls.',
      route: '/api-settings',
      cta: 'Go to API Settings',
      done: hasApiKeys,
    },
    {
      key: 'phone_number',
      label: 'Add a phone number',
      description: 'Import a Twilio number you own, or buy a new one, so your campaigns have a caller ID.',
      action: () => setShowAddPhoneNumber(true),
      cta: 'Add Phone Number',
      done: hasPhoneNumber,
    },
    {
      key: 'script',
      label: 'Create a script',
      description: 'Write the conversation your AI agent will follow.',
      route: '/script-management',
      cta: 'Create Script',
      done: hasScript,
    },
    {
      key: 'campaign',
      label: 'Launch a campaign',
      description: 'Set up your first outbound or inbound campaign.',
      route: '/campaigns',
      cta: 'Create Campaign',
      done: hasCampaign,
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const allDone = completedCount === steps.length;

  const handleDismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, 'true');
    setDismissed(true);
  };

  // The dialog stays mounted while it's open so that completing the phone
  // number step — which can hide the checklist below — doesn't unmount the
  // dialog mid-flight. Otherwise: hide while loading, dismissed, or all done.
  const hideChecklist = loading || dismissed || allDone;

  if (hideChecklist && !showAddPhoneNumber) return null;

  const addPhoneNumberDialog = (
    <AddPhoneNumberDialog
      isOpen={showAddPhoneNumber}
      onClose={() => setShowAddPhoneNumber(false)}
      onNumberAdded={fetchStatus}
    />
  );

  if (hideChecklist) return addPhoneNumberDialog;

  return (
    <>
    <Card className="border-brand-primary/30 bg-brand-primary/5">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-base">Get started</CardTitle>
            <span className="text-xs text-muted-foreground font-normal">
              {completedCount} of {steps.length} complete
            </span>
            {/* Progress bar */}
            <div className="hidden sm:flex items-center gap-1">
              {steps.map((step) => (
                <div
                  key={step.key}
                  className={`h-1.5 w-8 rounded-full transition-colors ${
                    step.done ? 'bg-brand-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground"
              onClick={() => setCollapsed((c) => !c)}
            >
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {!collapsed && (
          <CardDescription>
            Complete these steps to make your first AI call. Watch the <span className="font-medium text-foreground">Getting Started Video</span> in the Quick Start section below for a full walkthrough of the setup.
          </CardDescription>
        )}
      </CardHeader>

      {!collapsed && (
        <CardContent>
          <div className="space-y-3">
            {steps.map((step) => (
              <div
                key={step.key}
                className={`flex items-center justify-between gap-4 rounded-lg px-3 py-2.5 transition-colors ${
                  step.done ? 'bg-muted/40' : 'bg-background border'
                }`}
              >
                <div className="flex items-start gap-3">
                  {step.done ? (
                    <CheckCircle2 className="h-5 w-5 text-success mt-0.5 shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                  )}
                  <div>
                    <p className={`text-sm font-medium leading-none ${step.done ? 'line-through text-muted-foreground' : ''}`}>
                      {step.label}
                    </p>
                    {!step.done && (
                      <p className="text-xs text-muted-foreground mt-1">{step.description}</p>
                    )}
                  </div>
                </div>
                {!step.done && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0 text-xs h-7"
                    onClick={() => {
                      if (step.action) step.action();
                      else if (step.route) navigate(step.route);
                    }}
                  >
                    {step.cta}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
    {addPhoneNumberDialog}
    </>
  );
}
